import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import {
  Mail,
  Send,
  Loader2,
  Building2,
  Users,
  HardHat,
  CheckCircle,
  ExternalLink,
  Plus,
  X,
  Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SITE_URL = window.location.origin;

export default function SendUpdateEmailModal({ 
  open, 
  onClose, 
  entityType, // "project" or "maintenance"
  entityId,
  entityName,
  entityData // Optional: pass full entity data to get emails
}) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [entityDetails, setEntityDetails] = useState(null);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [customMessage, setCustomMessage] = useState("");

  // Fetch entity details to get contact emails
  useEffect(() => {
    if (open && entityId) {
      fetchEntityDetails();
    }
  }, [open, entityId]);

  const fetchEntityDetails = async () => {
    setLoading(true);
    try {
      const endpoint = entityType === "maintenance" 
        ? `${API}/admin/maintenance/${entityId}`
        : `${API}/admin/projects/${entityId}`;
      
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const details = data.maintenance_request || data.project || data;
        
        // Handle nested data structure (some requests have contact info in 'data' field)
        const nestedData = details.data || {};
        const mergedDetails = {
          ...details,
          contact_name: details.contact_name || nestedData.contact_name,
          contact_email: details.contact_email || nestedData.contact_email,
          contact_phone: details.contact_phone || nestedData.contact_phone,
          hospital_name: details.hospital_name || nestedData.hospital_name || details.facility_name,
          description: details.description || nestedData.description,
          request_type: details.request_type || nestedData.request_type,
          priority: details.priority || nestedData.priority
        };
        
        setEntityDetails(mergedDetails);
        
        // Pre-populate available emails
        const emails = [];
        if (mergedDetails.contact_email) {
          emails.push({ email: mergedDetails.contact_email, label: `Hospital: ${mergedDetails.contact_name || 'Contact'}`, type: 'hospital' });
        }
        if (details.hospital_email) {
          emails.push({ email: details.hospital_email, label: 'Hospital Admin', type: 'hospital' });
        }
        if (details.assigned_contractor_email) {
          emails.push({ email: details.assigned_contractor_email, label: `Contractor: ${details.assigned_contractor_name || 'Assigned'}`, type: 'contractor' });
        }
        if (details.additional_contacts) {
          details.additional_contacts.forEach(c => {
            if (c.email) {
              emails.push({ email: c.email, label: `${c.name || 'Contact'} (${c.role || 'Additional'})`, type: 'additional' });
            }
          });
        }
        
        // Remove duplicates
        const uniqueEmails = emails.filter((e, i, arr) => 
          arr.findIndex(x => x.email === e.email) === i
        );
        
        setAdditionalEmails(uniqueEmails);
        // Auto-select all hospital contacts
        setSelectedEmails(uniqueEmails.filter(e => e.type === 'hospital').map(e => e.email));
      }
    } catch (err) {
      console.error("Failed to fetch entity details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (additionalEmails.find(e => e.email === email)) {
      toast.error("Email already in list");
      return;
    }
    
    setAdditionalEmails([...additionalEmails, { email, label: email, type: 'manual' }]);
    setSelectedEmails([...selectedEmails, email]);
    setNewEmail("");
  };

  const handleToggleEmail = (email) => {
    if (selectedEmails.includes(email)) {
      setSelectedEmails(selectedEmails.filter(e => e !== email));
    } else {
      setSelectedEmails([...selectedEmails, email]);
    }
  };

  const handleRemoveEmail = (email) => {
    setAdditionalEmails(additionalEmails.filter(e => e.email !== email));
    setSelectedEmails(selectedEmails.filter(e => e !== email));
  };

  const generateEmailContent = () => {
    const ticketUrl = `${SITE_URL}/admin/maintenance?ticket=${entityId}`;
    const label = entityType === "maintenance" ? "Maintenance Request" : "Project";
    
    const subject = `Update: ${label} #${entityId} - ${entityName || entityDetails?.request_type || 'Status Update'}`;
    
    let body = `Hello,

This is an update regarding ${label} #${entityId}${entityName ? ` - ${entityName}` : ''}.

`;

    if (customMessage) {
      body += `UPDATE MESSAGE:
${customMessage}

`;
    }

    body += `TICKET DETAILS:
- Ticket ID: ${entityId}
- Type: ${entityDetails?.request_type || entityDetails?.issue_type || 'Maintenance'}
- Status: ${entityDetails?.status || 'Pending'}
- Stage: ${entityDetails?.stage || 'Intake'}
- Priority: ${entityDetails?.priority || 'Normal'}
- Location: ${entityDetails?.hospital_name || entityDetails?.facility_name || 'N/A'}

`;

    if (entityDetails?.description) {
      body += `DESCRIPTION:
${entityDetails.description}

`;
    }

    body += `VIEW TICKET:
${ticketUrl}

---
Sent via AskIsaac Platform
${user?.name || user?.email || 'Admin'}`;

    return { subject, body };
  };

  const handleOpenOutlook = () => {
    if (selectedEmails.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }

    const { subject, body } = generateEmailContent();
    // Use semicolon separator for Outlook compatibility
    const mailtoLink = `mailto:${selectedEmails.join(';')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.open(mailtoLink, '_blank');
    toast.success("Opening email client...");
    
    // Log the action
    logEmailSent();
  };

  const handleCopyEmails = () => {
    if (selectedEmails.length === 0) {
      toast.error("No emails selected");
      return;
    }
    navigator.clipboard.writeText(selectedEmails.join('; '));
    toast.success("Emails copied to clipboard");
  };

  const logEmailSent = async () => {
    try {
      // Log the email action to the activity log
      await fetch(`${API}/admin/maintenance-requests/${entityId}/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          note: `Email update sent to: ${selectedEmails.join(', ')}`
        })
      });
    } catch (err) {
      // Silent fail - just logging
    }
  };

  const handleClose = () => {
    setSelectedEmails([]);
    setAdditionalEmails([]);
    setCustomMessage("");
    setNewEmail("");
    setEntityDetails(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Send Update Email
          </DialogTitle>
          <DialogDescription>
            Select recipients and compose your update. Opens in your email client (Outlook/Gmail).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto text-blue-600 animate-spin mb-3" />
            <p className="text-sm text-slate-500">Loading contact information...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Ticket Info */}
            <div className="bg-slate-50 rounded-lg p-3 border">
              <p className="text-sm font-medium text-slate-700">
                {entityType === "maintenance" ? "Maintenance Request" : "Project"}: <span className="font-mono">#{entityId}</span>
              </p>
              {entityDetails && (
                <p className="text-xs text-slate-500 mt-1">
                  {entityDetails.request_type || entityDetails.issue_type} • {entityDetails.hospital_name || entityDetails.facility_name}
                </p>
              )}
            </div>

            {/* Recipients */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Recipients</Label>
              
              {additionalEmails.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {additionalEmails.map((item, idx) => (
                    <label 
                      key={idx} 
                      className={`flex items-center gap-3 p-2 border rounded-lg cursor-pointer transition-colors ${
                        selectedEmails.includes(item.email) ? 'bg-blue-50 border-blue-200' : 'hover:bg-slate-50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedEmails.includes(item.email)}
                        onCheckedChange={() => handleToggleEmail(item.email)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{item.label}</p>
                        <p className="text-xs text-slate-500 truncate">{item.email}</p>
                      </div>
                      {item.type === 'manual' && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                          onClick={(e) => { e.preventDefault(); handleRemoveEmail(item.email); }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic mb-3">No contacts found for this ticket</p>
              )}

              {/* Add Email */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Add email address..."
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                  className="flex-1"
                />
                <Button size="sm" variant="outline" onClick={handleAddEmail}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <Label>Update Message</Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Type your update message here... (ticket details will be included automatically)"
                rows={4}
                className="mt-1"
              />
            </div>

            {/* Selected count */}
            {selectedEmails.length > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {selectedEmails.length} recipient{selectedEmails.length !== 1 ? 's' : ''} selected
                </span>
                <Button size="sm" variant="ghost" onClick={handleCopyEmails}>
                  <Copy className="w-3 h-3 mr-1" />
                  Copy Emails
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleOpenOutlook}
            disabled={loading || selectedEmails.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in Email Client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
